<script lang="ts">
  /**
   * One level: what it asks for, the library it is written against, the
   * player's program, and the report.
   *
   * Names in the statement and in the "not defined yet" banner are links. A
   * name that is already defined leads to the level that defines it, and one
   * that nobody has defined yet leads to the level that asks for it, which is
   * exactly the level that has to be solved first.
   */
  import { chapterOf, levels, nextLevelId, positionOf, previousLevelId } from '../content';
  import { programText } from '../lib/format';
  import { parseStatement } from '../lib/markdown';
  import { holdCaret } from '../lib/focus';
  import { game } from '../state/game.svelte';
  import CodeEditor from './CodeEditor.svelte';
  import ConfirmButton from './ConfirmButton.svelte';
  import Problems from './Problems.svelte';
  import Report from './Report.svelte';

  const level = $derived(game.activeLevel);
  const chapter = $derived(chapterOf.get(level.id));
  const statement = $derived(parseStatement(level.body));
  const program = $derived(game.programFor(level.id));
  const diagnostics = $derived(game.diagnosticsFor(level.id));
  const context = $derived(game.contextFor(level.id));
  const groups = $derived(game.caseGroupsFor(level));
  const missing = $derived(game.missingRequirements(level));
  const report = $derived(game.currentReport);

  let showContext = $state(true);

  /**
   * The names the editor offers: this level's slots, then everything written in
   * this chapter or an earlier one. The library pane above shows the few of
   * them the level's cases reach.
   */
  const completions = $derived(game.suggestionsFor(level.id));

  /** Whether checking could tell the player anything new, and if not, why not. */
  const blocked = $derived(game.blockedReason(level));

  const position = $derived(positionOf(level.id));
  const previous = $derived(previousLevelId(level.id));
  const next = $derived(nextLevelId(level.id));

  // A page opens at the top of the pane, however the reader reached it: the
  // pane is the scroller, and the page is its first element.
  $effect(() => {
    void level.id;
    page?.scrollIntoView();
  });

  /** Where the report is rendered, so a finished check can be brought into view. */
  let reportAnchor: HTMLDivElement | undefined = $state();
  /** The page itself: the pane scrolls, and a new page starts at its top. */
  let page: HTMLElement | undefined = $state();

  /**
   * Checking, from the page as well as from the editor: both paths land here,
   * so the finished report is brought into view either way.
   */
  async function check(): Promise<void> {
    await game.checkActiveLevel();
    // Read the report once the check is done; a derived read here would be a
    // read from outside the render effect, which Svelte warns about.
    if (game.currentReport !== null) {
      // A report that finishes below the fold may as well not have run.
      requestAnimationFrame(() => {
        reportAnchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented || event.altKey) {
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      if (event.key === 'Enter') {
        event.preventDefault();
        void check();
      }
    }
  }

  /** The level a name leads to, when following it would go somewhere. */
  function linkFor(name: string): string | undefined {
    const owner = game.levelFor(name);
    return owner === undefined || owner === level.id ? undefined : owner;
  }

  /** What the pointer says about a name: where it is defined, or who asks for it. */
  function linkTitle(name: string, owner: string): string {
    return game.isDefined(name) ? `defined in level ${owner}` : `level ${owner} asks for this`;
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#snippet nameLink(name: string)}
  {@const owner = linkFor(name)}
  {#if owner === undefined}
    <code>{name}</code>
  {:else}
    <button
      class="namelink"
      title={linkTitle(name, owner)}
      onclick={() => game.openLevel(owner)}
    >{name}</button>
  {/if}
{/snippet}

<article class="page level" bind:this={page}>
  <header>
    <div class="where">
      <span>{chapter?.title ?? ''}</span>
      <span>{position} / {levels.length}</span>
    </div>
    <h1><span class="id">{level.id}</span> {level.title}</h1>
  </header>

  <div class="statement">
    {#each statement as block, blockIndex (blockIndex)}
      {#if block.kind === 'code'}
        <div class="statement-code">
          <CodeEditor value={block.text} readOnly lineNumbers={false} minHeight="0" />
        </div>
      {:else}
        <p>
          {#each block.parts as part, partIndex (partIndex)}
            {#if part.kind === 'code'}
              {@render nameLink(part.text)}
            {:else if part.kind === 'strong'}
              <strong>{part.text}</strong>
            {:else}
              {part.text}
            {/if}
          {/each}
        </p>
      {/if}
    {/each}
  </div>

  {#if level.lesson === true}
    <div class="actions">
      <button
        disabled={previous === undefined}
        title="Previous page ([)"
        onclick={() => game.openLevel(previous)}
      >
        ← Previous
      </button>
      <button
        disabled={next === undefined}
        title="Next page (])"
        onclick={() => game.openLevel(next)}
      >
        Next →
      </button>
    </div>
  {:else}
    {#if missing.length > 0}
      <p class="banner">
      This level needs
      {#each missing as name, index (name)}{#if index > 0}<span>, </span>{/if}{@render nameLink(
          name,
        )}{/each} {missing.length === 1 ? 'is' : 'are'} not defined yet.
    </p>
  {/if}

  {#if context.length > 0}
    <section class="context">
      <button class="section-head" aria-expanded={showContext} onclick={() => (showContext = !showContext)}>
        <span class="chevron" class:open={showContext}>▸</span>
        library
        <span class="count">{context.length} in scope</span>
      </button>
      {#if showContext}
        <CodeEditor
          value={programText(context)}
          names={completions}
          readOnly
          lineNumbers={false}
          minHeight="0"
          />
      {/if}
    </section>
  {/if}

  <section class="definition">
    <!--
      One editor per chapter, not per level: the file is the chapter's, and a
      fresh editor is what keeps Undo from walking back into the last topic the
      player was writing in.
    -->
    {#key chapter?.id ?? ''}
      <CodeEditor
        value={program}
        names={completions}
        {diagnostics}
        minHeight="6.5rem"
        onchange={(value) => game.setProgramFor(level.id, value)}
        onsubmit={() => void check()}
      />
    {/key}
    <Problems diagnostics={diagnostics} source={program} />
  </section>

  <!--
    The press is caught on the row rather than on each button: a button that is
    disabled does not pass its own events on, and a press on one of those would
    otherwise take the focus out of the editor for nothing. The listener refuses
    the focus and does nothing else, which is why the row is not interactive
    itself.
  -->
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <div class="actions" role="group" aria-label="Level actions" onmousedown={holdCaret}>
    <button
      class="primary"
      disabled={game.checking || blocked !== null}
      title={blocked ?? 'Check this level (Ctrl/Cmd + Enter)'}
      onclick={() => void check()}
    >
      {game.checking ? 'checking…' : 'Check'}
    </button>
    {#if game.checking}
      <button onclick={() => game.cancel()}>Stop</button>
    {/if}
    <button
      disabled={previous === undefined}
      title="Previous level ([)"
      onclick={() => game.openLevel(previous)}
    >
      ← Previous
    </button>
    <button
      disabled={next === undefined}
      title="Next level (])"
      onclick={() => game.openLevel(next)}
    >
      Next →
    </button>
    {#if program.trim() !== '' && chapter !== undefined}
      {#key level.id}
        <ConfirmButton
          variant="quiet"
          label="clear chapter"
          confirmLabel="erase?"
          title={`Erases this chapter`}
          onconfirm={() => chapter !== undefined && game.clearChapter(chapter.id)}
        />
      {/key}
    {/if}
    <span class="spacer"></span>
    {#if blocked}
      <span class="blocked">{blocked}</span>
    {:else}
      <span class="status">{game.statusOf(level.id)}</span>
    {/if}
  </div>

  {#if game.notice}
    <p class="banner" class:error={game.noticeFailed} aria-live="polite">{game.notice}</p>
  {/if}

    <div bind:this={reportAnchor}></div>
    {#if report !== null}
      <Report
        report={report}
        cases={level.cases}
        {groups}
        onopen={() => requestAnimationFrame(() => reportAnchor?.scrollIntoView({ behavior: 'smooth', block: 'start' }))}
      />
    {/if}
  {/if}
</article>

<style>
  header h1 {
    margin: 4px 0 12px;
  }

  .where {
    display: flex;
    justify-content: space-between;
    color: var(--text-faint);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .id {
    font-family: var(--mono);
    color: var(--text-dim);
  }

  /* The prose is a shade quieter than the names it talks about. */
  .statement p {
    margin: 0 0 10px;
    color: var(--text-dim);
  }

  /* The code of a statement is a read-only editor: the same face the player
   * writes their own definitions in, highlighting included. */
  .statement-code {
    margin: 0 0 12px;
  }

  /*
   * A name in prose is set in the monospace face rather than boxed: a chip's
   * padding would push the comma that usually follows it away from the word,
   * which reads as a space where none belongs. A link is the same name in the
   * accent colour with a dotted underline, which survives being next to
   * punctuation.
   */
  .statement code {
    font-family: var(--mono);
    font-size: 0.92em;
    color: var(--text);
    background: none;
    padding: 0;
  }

  .namelink {
    font-family: var(--mono);
    font-size: 0.92em;
    color: var(--accent);
    background: none;
    border: none;
    padding: 0;
    text-decoration: underline dotted;
    text-underline-offset: 0.18em;
  }

  .namelink:hover {
    text-decoration-style: solid;
  }

  .banner {
    background: var(--bg-raised);
    border: 1px solid var(--border-strong);
    border-left: 3px solid var(--warn);
    border-radius: 6px;
    padding: 8px 12px;
    color: var(--text-dim);
    font-size: 13px;
    margin: 12px 0;
  }

  .banner.error {
    border-left-color: var(--fail);
  }

  section {
    margin-top: 16px;
  }

  /*
   * The library is a reference the player folds away, not a part of the level,
   * so the block sits closer to the statement above it and to the editor below
   * it than the page's sections do.
   */
  .context,
  .definition {
    margin-top: 10px;
  }

  .section-head {
    display: flex;
    align-items: baseline;
    gap: 8px;
    width: 100%;
    background: none;
    border: none;
    padding: 4px 2px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-faint);
  }

  .chevron {
    display: inline-block;
    transition: transform 0.12s ease-out;
  }

  .chevron.open {
    transform: rotate(90deg);
  }

  .count {
    margin-left: auto;
    text-transform: none;
    letter-spacing: 0;
    font-family: var(--mono);
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 14px;
  }

  /*
   * A button that cannot be pressed still takes the focus when it is clicked,
   * which would take the caret out of the editor for a press that does
   * nothing. Letting the press through to the row, which refuses to take the
   * focus in the first place, keeps the caret where it was.
   */
  .actions button:disabled {
    pointer-events: none;
  }

  .spacer {
    flex: 1;
  }

  .status {
    color: var(--text-faint);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .blocked {
    color: var(--warn);
    font-size: 12.5px;
  }
</style>
